import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { authMe, authLogout, adminOverview, adminOrders, adminUpdateStatus, adminSendProof, adminDesigns, adminGetSettings, adminPutSettings, adminAiDigestTest, adminList, adminCreate, adminUpdate, adminDelete, productionPackUrl, artworkSvgUrl, adminGetAiPrompts, adminPutAiPrompts, adminEffectivePrompts, adminPreviewAiPrompt, adminFxRefresh } from "../lib/api";

const money = (v, sym = "£") => sym + Number(v || 0).toFixed(2);
const STATUSES = ["paid", "artwork_review", "awaiting_approval", "artwork_approved", "in_production", "quality_check", "dispatched", "completed", "cancelled"];
const TABS = ["Overview", "Orders", "Designs", "Pricing", "Turf Colours", "Graphic Colours", "Graphics", "Templates", "AI Prompts"];

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const login = () => {
  const redirectUrl = window.location.origin + "/admin";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("Overview");

  useEffect(() => { authMe().then((u) => setUser(u)).catch(() => setUser(null)).finally(() => setChecking(false)); }, []);

  if (checking) return <div className="min-h-screen bg-pgt-bg flex items-center justify-center"><span className="text-pgt-green font-display text-xl tracking-widest pgt-pulse">LOADING…</span></div>;

  if (!user || !user.is_admin) {
    return (
      <div className="min-h-screen bg-pgt-bg flex items-center justify-center p-6">
        <div className="border border-white/10 bg-pgt-surface rounded-sm p-8 max-w-sm w-full text-center">
          <div className="w-10 h-1 bg-pgt-green mx-auto mb-5" />
          <h1 className="pgt-heading text-white text-3xl mb-2">Admin Access</h1>
          <p className="text-white/50 text-sm mb-6">{user && !user.is_admin ? "This account is not authorised for admin." : "Sign in to manage Prime Gym Turf."}</p>
          <button onClick={login} data-testid="admin-login-btn" className="w-full bg-pgt-green text-black font-display font-bold uppercase tracking-wider py-3 rounded-sm hover:bg-pgt-greenHover">Sign in with Google</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-pgt-bg">
      <header className="border-b border-white/10 sticky top-0 bg-pgt-bg z-40">
        <div className="max-w-[1500px] mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <span className="font-display font-extrabold tracking-widest text-white">PRIME GYM TURF · ADMIN</span>
          <button onClick={() => authLogout().then(() => setUser(null))} data-testid="admin-logout-btn" className="text-white/60 hover:text-white text-sm">Logout</button>
        </div>
      </header>
      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 py-6">
        <div className="flex gap-1 flex-wrap mb-6 border-b border-white/10">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t.replace(/\s+/g, "-").toLowerCase()}`}
              className={`px-4 py-2.5 font-display font-bold uppercase tracking-wide text-sm border-b-2 -mb-px transition-colors ${tab === t ? "border-pgt-green text-pgt-green" : "border-transparent text-white/50 hover:text-white"}`}>{t}</button>
          ))}
        </div>
        {tab === "Overview" && <Overview />}
        {tab === "Orders" && <Orders />}
        {tab === "Designs" && <Designs />}
        {tab === "Pricing" && <Pricing />}
        {tab === "Turf Colours" && <Colours kind="turf-colours" title="Turf Colours" />}
        {tab === "Graphic Colours" && <Colours kind="graphic-colours" title="Graphic Colours" />}
        {tab === "Graphics" && <Graphics />}
        {tab === "Templates" && <Templates />}
        {tab === "AI Prompts" && <AiPrompts />}
      </div>
    </div>
  );
}

const Card = ({ children, className = "" }) => <div className={`border border-white/10 bg-pgt-surface rounded-sm ${className}`}>{children}</div>;

function Overview() {
  const [o, setO] = useState(null);
  useEffect(() => { adminOverview().then(setO).catch(() => {}); }, []);
  if (!o) return null;
  return (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-3 gap-4">
        {[["Orders", o.orders], ["Saved Designs / Leads", o.designs], ["Revenue", money(o.revenue)]].map(([l, v]) => (
          <Card key={l} className="p-5"><div className="text-xs uppercase tracking-wider text-white/50">{l}</div><div className="font-display font-extrabold text-3xl text-white mt-1">{v}</div></Card>
        ))}
      </div>
      <Card className="p-5">
        <h3 className="font-display font-bold uppercase tracking-wider text-white mb-3">Orders by status</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {STATUSES.map((s) => <div key={s} className="flex justify-between text-sm text-white/60 border border-white/5 rounded-sm px-3 py-2"><span className="capitalize">{s.replace(/_/g, " ")}</span><span className="text-white">{o.by_status[s] || 0}</span></div>)}
        </div>
      </Card>
    </div>
  );
}

function Orders() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const load = () => adminOrders(filter).then(setRows).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const changeStatus = async (num, status) => { await adminUpdateStatus(num, { status }); toast.success("Status updated"); load(); };
  const setTracking = async (num) => { const t = window.prompt("Tracking info:"); if (t != null) { await adminUpdateStatus(num, { status: "dispatched", tracking: t }); toast.success("Saved"); load(); } };
  const sendProof = async (num) => { await adminSendProof(num); toast.success("Proof email sent"); load(); };

  return (
    <div>
      <div className="flex gap-2 mb-4 flex-wrap">
        {["all", ...STATUSES].map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`order-filter-${s}`} className={`px-3 py-1.5 text-xs font-display font-bold uppercase rounded-sm ${filter === s ? "bg-pgt-green text-black" : "bg-white/5 text-white/60 hover:text-white"}`}>{s.replace(/_/g, " ")}</button>
        ))}
      </div>
      <div className="space-y-3">
        {rows.length === 0 && <div className="text-white/40 text-sm">No orders.</div>}
        {rows.map((o) => (
          <Card key={o.order_number} className="p-4" data-testid={`order-row-${o.order_number}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="font-display font-bold text-white text-lg">{o.order_number} <span className="text-white/40 text-sm font-body">· {o.contact?.name} ({o.contact?.email})</span></div>
                <div className="text-white/60 text-sm">{o.design.dimensions.length}m × {o.design.dimensions.width}m · {o.production_label} · {money(o.price.total, o.price.currency_symbol)}</div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <select value={o.status} onChange={(e) => changeStatus(o.order_number, e.target.value)} data-testid={`order-status-select-${o.order_number}`} className="bg-pgt-bg border border-white/15 rounded-sm px-2 py-1.5 text-white text-sm">
                  {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                </select>
                <button onClick={() => sendProof(o.order_number)} className="text-xs border border-white/15 text-white/70 px-3 py-1.5 rounded-sm hover:border-pgt-green hover:text-pgt-green">Send Proof</button>
                <button onClick={() => setTracking(o.order_number)} className="text-xs border border-white/15 text-white/70 px-3 py-1.5 rounded-sm hover:border-pgt-green hover:text-pgt-green">Tracking</button>
                <a href={productionPackUrl(o.order_number)} target="_blank" rel="noreferrer" className="text-xs bg-white/10 text-white px-3 py-1.5 rounded-sm hover:bg-white/20">PDF Pack</a>
                <a href={artworkSvgUrl(o.order_number)} target="_blank" rel="noreferrer" className="text-xs bg-white/10 text-white px-3 py-1.5 rounded-sm hover:bg-white/20">SVG</a>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function Designs() {
  const [rows, setRows] = useState([]);
  useEffect(() => { adminDesigns().then(setRows).catch(() => {}); }, []);
  return (
    <div className="space-y-3">
      {rows.length === 0 && <div className="text-white/40 text-sm">No saved designs yet.</div>}
      {rows.map((d) => (
        <Card key={d.design_id} className="p-4 flex flex-wrap justify-between gap-3" data-testid={`design-row-${d.design_id}`}>
          <div>
            <div className="font-display font-bold text-white">{d.design_id} · {d.name}</div>
            <div className="text-white/60 text-sm">{d.email}{d.phone ? ` · ${d.phone}` : ""} · {d.design?.dimensions?.length}m × {d.design?.dimensions?.width}m · ~{money(d.price?.total)}</div>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className={`px-2 py-1 rounded-sm ${d.checkout_started ? "bg-yellow-600/20 text-yellow-400" : "bg-white/5 text-white/40"}`}>Checkout {d.checkout_started ? "yes" : "no"}</span>
            <span className={`px-2 py-1 rounded-sm ${d.purchased ? "bg-pgt-green/20 text-pgt-green" : "bg-white/5 text-white/40"}`}>Purchased {d.purchased ? "yes" : "no"}</span>
          </div>
        </Card>
      ))}
    </div>
  );
}

function Pricing() {
  const [s, setS] = useState(null);
  useEffect(() => { adminGetSettings().then(setS).catch(() => {}); }, []);
  if (!s) return null;
  const upd = (grp, k, v) => setS((p) => ({ ...p, [grp]: { ...p[grp], [k]: v } }));
  const num = (grp, k, v) => upd(grp, k, parseFloat(v) || 0);
  const save = async () => { await adminPutSettings({ pricing: s.pricing, size_limits: s.size_limits, production: s.production, contact: s.contact, ai_digest: s.ai_digest }); toast.success("Settings saved"); };
  const updDigest = (k, v) => setS((p) => ({ ...p, ai_digest: { ...(p.ai_digest || {}), [k]: v } }));
  const sendTestDigest = async () => { try { const r = await adminAiDigestTest(); toast.success(r.enabled ? `Digest sent to ${r.sent} recipient(s)` : "Digest is disabled"); } catch (e) { toast.error("Could not send digest"); } };
  const dg = s.ai_digest || { enabled: true, recipients: [], image_cost_gbp: 0 };
  const cur = s.pricing.currencies || {};
  const setCur = (path, value) => setS((p) => {
    const c = JSON.parse(JSON.stringify(p.pricing.currencies || {}));
    const parts = path.split("."); let o = c;
    for (let i = 0; i < parts.length - 1; i++) { o[parts[i]] = o[parts[i]] || {}; o = o[parts[i]]; }
    o[parts[parts.length - 1]] = value;
    return { ...p, pricing: { ...p.pricing, currencies: c } };
  });
  const refreshFx = async () => {
    try {
      const r = await adminFxRefresh();
      if (r.updated) { toast.success(`Rates updated via ${r.fx?.source}`); if (r.fx) setCur("fx", r.fx); }
      else toast.message(r.reason || "No update");
    } catch { toast.error("FX refresh failed"); }
  };

  const pf = [["standard_base", "Standard base £"], ["standard_per_sqm", "Standard £ / m²"], ["express_base", "Express base £"], ["express_per_sqm", "Express £ / m²"], ["min_price", "Minimum project £"], ["offer_discount_pct", "Launch discount %"], ["round_to", "Round to nearest £"]];
  const sf = [["min_length", "Min length"], ["max_length", "Max length"], ["min_width", "Min width"], ["max_width", "Max width"], ["increment", "Increment"]];

  return (
    <div className="space-y-6 max-w-3xl">
      <Card className="p-5">
        <h3 className="font-display font-bold uppercase tracking-wider text-white mb-4">Pricing Engine</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          {pf.map(([k, l]) => (
            <div key={k}><label className="text-xs uppercase tracking-wider text-white/50">{l}</label>
              <input type="number" step="0.01" value={s.pricing[k]} onChange={(e) => num("pricing", k, e.target.value)} data-testid={`pricing-${k}`} className="w-full mt-1 bg-pgt-bg border border-white/15 rounded-sm px-3 py-2 text-white outline-none focus:border-pgt-green" /></div>
          ))}
          <div><label className="text-xs uppercase tracking-wider text-white/50">Offer label</label>
            <input value={s.pricing.offer_label || ""} onChange={(e) => upd("pricing", "offer_label", e.target.value)} data-testid="pricing-offer-label" className="w-full mt-1 bg-pgt-bg border border-white/15 rounded-sm px-3 py-2 text-white outline-none focus:border-pgt-green" /></div>
        </div>
        <div className="flex gap-6 mt-4">
          <label className="flex items-center gap-2 text-sm text-white/70"><input type="checkbox" checked={!!s.pricing.offer_active} onChange={(e) => upd("pricing", "offer_active", e.target.checked)} data-testid="pricing-offer-active" className="accent-[#00D26A]" /> Launch offer active</label>
          <label className="flex items-center gap-2 text-sm text-white/70"><input type="checkbox" checked={s.pricing.is_placeholder} onChange={(e) => upd("pricing", "is_placeholder", e.target.checked)} data-testid="pricing-placeholder" className="accent-[#00D26A]" /> Placeholder / test prices</label>
        </div>
      </Card>
      <Card className="p-5">
        <h3 className="font-display font-bold uppercase tracking-wider text-white mb-1">Currencies & International Pricing</h3>
        <p className="text-white/40 text-xs mb-4">GBP is the master price. Order of operations: master GBP → regional adjustment (in GBP) → FX conversion → rounding. UK pricing is never changed.</p>
        <div className="text-xs uppercase tracking-wider text-pgt-green mb-2">Enabled currencies</div>
        <div className="flex gap-6 mb-5">
          {["GBP", "USD", "EUR"].map((c) => (
            <label key={c} className="flex items-center gap-2 text-sm text-white/70">
              <input type="checkbox" checked={!!(cur.enabled || {})[c]} onChange={(e) => setCur(`enabled.${c}`, e.target.checked)} data-testid={`ccy-enabled-${c}`} className="accent-[#00D26A]" /> {c}
            </label>
          ))}
        </div>
        <div className="text-xs uppercase tracking-wider text-pgt-green mb-2">Exchange rates</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><label className="text-xs uppercase tracking-wider text-white/50">GBP → USD</label>
            <input type="number" step="0.0001" value={(cur.fx || {}).GBP_USD ?? ""} onChange={(e) => setCur("fx.GBP_USD", parseFloat(e.target.value) || 0)} data-testid="fx-gbp-usd" className="w-full mt-1 bg-pgt-bg border border-white/15 rounded-sm px-3 py-2 text-white outline-none focus:border-pgt-green" /></div>
          <div><label className="text-xs uppercase tracking-wider text-white/50">GBP → EUR</label>
            <input type="number" step="0.0001" value={(cur.fx || {}).GBP_EUR ?? ""} onChange={(e) => setCur("fx.GBP_EUR", parseFloat(e.target.value) || 0)} data-testid="fx-gbp-eur" className="w-full mt-1 bg-pgt-bg border border-white/15 rounded-sm px-3 py-2 text-white outline-none focus:border-pgt-green" /></div>
        </div>
        <div className="flex items-center gap-5 mt-3 flex-wrap">
          <label className="flex items-center gap-2 text-sm text-white/70"><input type="checkbox" checked={!!(cur.fx || {}).auto} onChange={(e) => setCur("fx.auto", e.target.checked)} data-testid="fx-auto" className="accent-[#00D26A]" /> Auto-update daily</label>
          <span className="text-white/40 text-xs">Last updated: {(cur.fx || {}).updated_at ? new Date(cur.fx.updated_at).toLocaleString("en-GB") : "never"}{(cur.fx || {}).source ? ` · ${cur.fx.source}` : ""}</span>
          <button onClick={refreshFx} data-testid="fx-refresh-btn" className="border border-pgt-green/50 text-pgt-green font-display font-bold uppercase tracking-wider text-xs px-4 py-2 rounded-sm hover:bg-pgt-green/10">Refresh Rates Now</button>
        </div>
        <div className="text-xs uppercase tracking-wider text-pgt-green mt-5 mb-2">Regional adjustments (applied in GBP before conversion)</div>
        <div className="space-y-2">
          {[["UK", "UK"], ["USA", "USA"], ["EUR", "Europe"]].map(([k, label]) => (
            <div key={k} className="grid grid-cols-[70px_1fr_1fr] gap-3 items-end">
              <span className="text-white/70 text-sm pb-2">{label}</span>
              <div><label className="text-[10px] uppercase tracking-wider text-white/40">Price × multiplier</label>
                <input type="number" step="0.01" value={((cur.regional || {})[k] || {}).multiplier ?? 1} onChange={(e) => setCur(`regional.${k}.multiplier`, parseFloat(e.target.value) || 0)} data-testid={`reg-mult-${k}`} className="w-full mt-1 bg-pgt-bg border border-white/15 rounded-sm px-3 py-2 text-white outline-none focus:border-pgt-green" /></div>
              <div><label className="text-[10px] uppercase tracking-wider text-white/40">Surcharge £ (GBP)</label>
                <input type="number" step="1" value={((cur.regional || {})[k] || {}).surcharge ?? 0} onChange={(e) => setCur(`regional.${k}.surcharge`, parseFloat(e.target.value) || 0)} data-testid={`reg-sur-${k}`} className="w-full mt-1 bg-pgt-bg border border-white/15 rounded-sm px-3 py-2 text-white outline-none focus:border-pgt-green" /></div>
            </div>
          ))}
        </div>
        <div className="text-xs uppercase tracking-wider text-pgt-green mt-5 mb-2">Rounding (to nearest)</div>
        <div className="grid grid-cols-3 gap-3">
          {["GBP", "USD", "EUR"].map((c) => (
            <div key={c}><label className="text-xs uppercase tracking-wider text-white/50">{c}</label>
              <input type="number" step="1" value={(cur.rounding || {})[c] ?? 5} onChange={(e) => setCur(`rounding.${c}`, parseInt(e.target.value) || 1)} data-testid={`round-${c}`} className="w-full mt-1 bg-pgt-bg border border-white/15 rounded-sm px-3 py-2 text-white outline-none focus:border-pgt-green" /></div>
          ))}
        </div>
      </Card>
      <Card className="p-5">
        <h3 className="font-display font-bold uppercase tracking-wider text-white mb-4">Size Limits</h3>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {sf.map(([k, l]) => (<div key={k}><label className="text-xs uppercase tracking-wider text-white/50">{l}</label><input type="number" step="0.1" value={s.size_limits[k]} onChange={(e) => num("size_limits", k, e.target.value)} data-testid={`limit-${k}`} className="w-full mt-1 bg-pgt-bg border border-white/15 rounded-sm px-3 py-2 text-white outline-none focus:border-pgt-green" /></div>))}
        </div>
      </Card>
      <Card className="p-5">
        <h3 className="font-display font-bold uppercase tracking-wider text-white mb-4">Production & Contact</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          {[["production", "standard_time", "Standard time"], ["production", "express_time", "Express time"], ["contact", "quote_url", "Quote URL"], ["contact", "phone", "Phone"]].map(([g, k, l]) => (
            <div key={k}><label className="text-xs uppercase tracking-wider text-white/50">{l}</label><input value={s[g][k] || ""} onChange={(e) => upd(g, k, e.target.value)} className="w-full mt-1 bg-pgt-bg border border-white/15 rounded-sm px-3 py-2 text-white outline-none focus:border-pgt-green" /></div>
          ))}
        </div>
      </Card>
      <Card className="p-5">
        <h3 className="font-display font-bold uppercase tracking-wider text-white mb-1">AI Weekly Digest</h3>
        <p className="text-white/40 text-xs mb-4">A simple weekly owner email summarising AI Design usage, GPT Image 2 cost and conversion. Sent every Monday.</p>
        <label className="flex items-center gap-2 text-sm text-white/70 mb-4"><input type="checkbox" checked={!!dg.enabled} onChange={(e) => updDigest("enabled", e.target.checked)} data-testid="digest-enabled" className="accent-[#00D26A]" /> Send the weekly digest</label>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><label className="text-xs uppercase tracking-wider text-white/50">Recipient emails (comma separated)</label>
            <input value={(dg.recipients || []).join(", ")} onChange={(e) => updDigest("recipients", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} data-testid="digest-recipients" className="w-full mt-1 bg-pgt-bg border border-white/15 rounded-sm px-3 py-2 text-white outline-none focus:border-pgt-green" /></div>
          <div><label className="text-xs uppercase tracking-wider text-white/50">GPT Image 2 cost per image £ (for cost estimate)</label>
            <input type="number" step="0.001" value={dg.image_cost_gbp ?? 0} onChange={(e) => updDigest("image_cost_gbp", parseFloat(e.target.value) || 0)} data-testid="digest-cost" className="w-full mt-1 bg-pgt-bg border border-white/15 rounded-sm px-3 py-2 text-white outline-none focus:border-pgt-green" /></div>
        </div>
        <button onClick={sendTestDigest} data-testid="digest-test-btn" className="mt-4 border border-pgt-green/50 text-pgt-green font-display font-bold uppercase tracking-wider text-sm px-5 py-2 rounded-sm hover:bg-pgt-green/10">Send Test Digest Now</button>
      </Card>
      <button onClick={save} data-testid="save-settings-btn" className="bg-pgt-green text-black font-display font-bold uppercase tracking-wider px-8 py-3 rounded-sm hover:bg-pgt-greenHover">Save Settings</button>
    </div>
  );
}

function Colours({ kind, title }) {
  const [rows, setRows] = useState([]);
  const load = () => adminList(kind).then(setRows).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);
  const add = async () => { await adminCreate(kind, { name: "New Colour", hex: "#888888", available: true }); load(); };
  const upd = async (id, patch) => { await adminUpdate(kind, id, patch); load(); };
  const del = async (id) => { await adminDelete(kind, id); load(); };
  return (
    <div className="max-w-2xl space-y-2">
      <button onClick={add} data-testid={`add-${kind}`} className="mb-2 bg-pgt-green text-black font-display font-bold uppercase text-sm px-4 py-2 rounded-sm">+ Add Colour</button>
      {rows.map((c) => (
        <Card key={c.item_id} className="p-3 flex items-center gap-3" data-testid={`colour-${c.item_id}`}>
          <input type="color" value={c.hex} onChange={(e) => upd(c.item_id, { hex: e.target.value })} className="w-9 h-9 bg-transparent cursor-pointer" />
          <input value={c.name} onChange={(e) => setRows((p) => p.map((x) => x.item_id === c.item_id ? { ...x, name: e.target.value } : x))} onBlur={(e) => upd(c.item_id, { name: e.target.value })} className="flex-1 bg-pgt-bg border border-white/15 rounded-sm px-3 py-1.5 text-white outline-none" />
          <label className="flex items-center gap-1 text-xs text-white/60"><input type="checkbox" checked={c.available} onChange={(e) => upd(c.item_id, { available: e.target.checked })} className="accent-[#00D26A]" /> Available</label>
          <button onClick={() => del(c.item_id)} className="text-red-400 text-sm hover:text-red-300">Delete</button>
        </Card>
      ))}
    </div>
  );
}

function Graphics() {
  const [rows, setRows] = useState([]);
  const load = () => adminList("graphics").then(setRows).catch(() => {});
  useEffect(() => { load(); }, []);
  const upd = async (id, patch) => { await adminUpdate("graphics", id, patch); load(); };
  const del = async (id) => { await adminDelete("graphics", id); load(); };
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
      {rows.map((g) => (
        <Card key={g.item_id} className="p-3 flex items-center justify-between gap-2" data-testid={`graphic-${g.kind}`}>
          <div><div className="text-white text-sm font-display font-bold uppercase">{g.name}</div><div className="text-white/40 text-xs">{g.category}</div></div>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1 text-xs text-white/60"><input type="checkbox" checked={g.available} onChange={(e) => upd(g.item_id, { available: e.target.checked })} className="accent-[#00D26A]" /> On</label>
            <button onClick={() => del(g.item_id)} className="text-red-400 text-xs hover:text-red-300">Del</button>
          </div>
        </Card>
      ))}
    </div>
  );
}

function Templates() {
  const [rows, setRows] = useState([]);
  const load = () => adminList("templates").then(setRows).catch(() => {});
  useEffect(() => { load(); }, []);
  const del = async (id) => { await adminDelete("templates", id); load(); };
  return (
    <div className="space-y-2 max-w-2xl">
      {rows.map((t) => (
        <Card key={t.item_id} className="p-4 flex justify-between gap-3" data-testid={`template-row-${t.item_id}`}>
          <div><div className="font-display font-bold uppercase text-white">{t.name}</div><div className="text-white/50 text-sm">{t.description}</div></div>
          <button onClick={() => del(t.item_id)} className="text-red-400 text-sm hover:text-red-300 h-fit">Delete</button>
        </Card>
      ))}
      <p className="text-white/40 text-xs">Templates populate the customer editor and stay fully editable. New templates can be added via the API.</p>
    </div>
  );
}

const FAMILY_KEYS = [
  ["clean_modern_performance", "Clean Modern Performance"],
  ["futuristic_precision", "Futuristic Precision"],
  ["sprint_performance", "Sprint Performance"],
  ["premium_minimal", "Premium Minimal"],
  ["bold_geometric", "Bold Geometric"],
];
const DIRECTION_OPTS = [...FAMILY_KEYS, ["ai_choice", "AI Designer's Choice"]];
const MARK_OPTS = ["none", "metre_markings", "numbers", "markings_numbers", "boxed_numbers", "sprint_track", "lane_training", "ai_choice"];
const GLOBALS = [["master", "Master Quality Prompt"], ["full_length", "Full-Length Note"], ["grid_first", "Measured Grid-First Rule"], ["logo_scale", "Logo Size Lock"], ["spec_lock", "Customer Specification Lock"], ["base_colour", "Base Surface Colour Lock"], ["integrated", "Integrated / Output Note"], ["negative", "Negative / Prohibited Note"]];
const MOTIF_KEYS = [["auto", "AI Choose"], ["chevron", "Chevron"], ["arrow", "Arrow"], ["hexagon", "Hexagon"], ["circle", "Circle"], ["ring", "Ring"], ["diamond", "Diamond"], ["triangle", "Triangle"], ["block", "Block"], ["angular", "Angular"]];
const MARK_BODY_KEYS = [["metre_markings", "Metre markings only"], ["numbers", "Numbers only"], ["markings_numbers", "Markings + numbers"], ["boxed_numbers", "Boxed numbers"], ["sprint_track", "Sprint track"], ["lane_training", "Lane / training track"], ["ai_choice", "AI Choose"]];
const MARK_GLOBALS = [["none", "No markings"], ["orient", "Marking Geometry (CRITICAL — yard-line orientation)"], ["hd", "Marking Quality Note"], ["physical", "Metre Positions / Interval ({L} {count} {seq} {iv})"]];
const MARK_POSITION_KEYS = [["centre", "Centre"], ["both_sides", "Both sides"], ["lane_based", "Lane based"]];
const MARK_SF_KEYS = [["start_line", "Start line"], ["finish_line", "Finish line"], ["sf_text", "Start/Finish text"], ["none", "No start/finish (default)"]];
const STRUCTURE_KEYS = [["single", "Single lane"], ["two_lane", "Two lane ({W} {lane})"], ["lane_shared", "Lane copy — shared"], ["lane_per_lane", "Lane copy — per lane"]];

function Field({ label, value, onChange, rows = 4, testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-white/50">{label}</label>
      <textarea data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} rows={rows}
        className="w-full mt-1 bg-pgt-bg border border-white/15 rounded-sm px-3 py-2 text-white/90 text-xs leading-relaxed font-mono" />
    </div>
  );
}

function AiPrompts() {
  const [cfg, setCfg] = useState(null);
  const [fam, setFam] = useState("clean_modern_performance");
  const [motifSel, setMotifSel] = useState("hexagon");
  const [bodySel, setBodySel] = useState("markings_numbers");
  const [sample, setSample] = useState({
    dimensions: { length: 10, width: 1.5 }, direction: "clean_modern_performance", motif: "auto",
    intensity: "balanced", layout: "single", brand_colours: ["#111111", "#00D26A", "#f5f5f0"],
    has_logo: true, instructions: "",
    markings: { style: "markings_numbers", interval: 1, position: "centre", lane_mode: "shared", start_line: false, finish_line: false, sf_text: false },
  });
  const [preview, setPreview] = useState(null);
  const [effective, setEffective] = useState([]);
  const [openEff, setOpenEff] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { adminGetAiPrompts().then(setCfg).catch(() => {}); adminEffectivePrompts().then(setEffective).catch(() => {}); }, []);

  // live preview — debounced whenever the (unsaved) cfg or sample selections change
  useEffect(() => {
    if (!cfg) return;
    const id = setTimeout(() => {
      adminPreviewAiPrompt({ cfg, sample }).then(setPreview).catch(() => setPreview(null));
    }, 450);
    return () => clearTimeout(id);
  }, [cfg, sample]);

  if (!cfg) return null;
  const setG = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const setFamField = (k, v) => setCfg((c) => ({ ...c, families: { ...c.families, [fam]: { ...c.families[fam], [k]: v } } }));
  const setRoutingWeight = (f, v) => setCfg((c) => ({ ...c, routing: { ...c.routing, weights: { ...c.routing.weights, [f]: Math.max(0, parseInt(v) || 0) } } }));
  const setRoutingDefault = (f) => setCfg((c) => ({ ...c, routing: { ...c.routing, default_family: f } }));
  const setMotifTxt = (k, v) => setCfg((c) => ({ ...c, motifs: { ...c.motifs, [k]: v } }));
  const setMkGlobal = (k, v) => setCfg((c) => ({ ...c, markings: { ...c.markings, [k]: v } }));
  const setMkBody = (k, v) => setCfg((c) => ({ ...c, markings: { ...c.markings, bodies: { ...c.markings.bodies, [k]: v } } }));
  const setMkPos = (k, v) => setCfg((c) => ({ ...c, markings: { ...c.markings, positions: { ...c.markings.positions, [k]: v } } }));
  const setMkSf = (k, v) => setCfg((c) => ({ ...c, markings: { ...c.markings, startfinish: { ...c.markings.startfinish, [k]: v } } }));
  const setStruct = (k, v) => setCfg((c) => ({ ...c, structure: { ...c.structure, [k]: v } }));
  const setS = (patch) => setSample((s) => ({ ...s, ...patch }));
  const setMk = (patch) => setSample((s) => ({ ...s, markings: { ...s.markings, ...patch } }));

  const save = async () => { setSaving(true); try { const r = await adminPutAiPrompts(cfg); setCfg(r); toast.success("AI prompts saved"); } catch { toast.error("Save failed"); } setSaving(false); };
  const reset = async () => { if (!window.confirm("Reset ALL AI prompts to the built-in defaults?")) return; try { const r = await adminPutAiPrompts({ reset: true }); setCfg(r); toast.success("Reset to defaults"); } catch { toast.error("Reset failed"); } };
  const refreshEff = () => adminEffectivePrompts().then(setEffective).catch(() => {});

  const Pill = ({ on, children, ...p }) => <button {...p} className={`px-2.5 py-1 rounded-sm text-xs font-display uppercase border ${on ? "border-pgt-green text-pgt-green bg-pgt-green/10" : "border-white/15 text-white/60 hover:border-white/40"}`}>{children}</button>;

  return (
    <div className="space-y-6" data-testid="admin-ai-prompts">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="pgt-heading text-white text-2xl">AI Prompt Studio</h2>
        <div className="flex gap-2">
          <button data-testid="ai-prompts-reset" onClick={reset} className="border border-white/20 text-white/70 text-sm font-display uppercase px-4 py-2 rounded-sm hover:border-red-400 hover:text-red-300">Reset Defaults</button>
          <button data-testid="ai-prompts-save" disabled={saving} onClick={save} className="bg-pgt-green text-black font-display font-bold uppercase text-sm px-5 py-2 rounded-sm disabled:opacity-50">{saving ? "Saving…" : "Save Prompts"}</button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* EDITOR */}
        <div className="space-y-4">
          <Card className="p-4 space-y-3">
            <div className="text-xs uppercase tracking-wider text-pgt-green">Global Prompt Blocks</div>
            {GLOBALS.map(([k, lbl]) => <Field key={k} testid={`ai-prompt-${k}`} label={lbl} value={cfg[k]} onChange={(v) => setG(k, v)} rows={k === "master" ? 6 : (k === "spec_lock" ? 14 : 3)} />)}
          </Card>
          <Card className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-wider text-pgt-green">Style Family</div>
              <select data-testid="ai-prompt-family-select" value={fam} onChange={(e) => setFam(e.target.value)} className="bg-pgt-bg border border-white/15 rounded-sm px-2 py-1 text-white text-xs">
                {FAMILY_KEYS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>
            <Field testid="ai-prompt-family-label" label="Label" value={cfg.families[fam]?.label} onChange={(v) => setFamField("label", v)} rows={1} />
            <Field testid="ai-prompt-family-prompt" label="Family Prompt" value={cfg.families[fam]?.prompt} onChange={(v) => setFamField("prompt", v)} rows={6} />
          </Card>

          <Card className="p-4 space-y-3" data-testid="ai-prompt-routing">
            <div className="text-xs uppercase tracking-wider text-pgt-green">AI Designer's Choice — Routing</div>
            <p className="text-[11px] text-white/40">When a customer picks "AI Designer's Choice", a style family is chosen at random using these weights (higher = picked more often). If all weights are 0, the default family is always used.</p>
            <div className="grid grid-cols-2 gap-2">
              {FAMILY_KEYS.map(([k, l]) => (
                <label key={k} className="text-[11px] text-white/60 flex items-center justify-between gap-2 bg-pgt-bg border border-white/15 rounded-sm px-2 py-1">
                  <span className="truncate">{l}</span>
                  <input data-testid={`ai-routing-weight-${k}`} type="number" min="0" value={cfg.routing?.weights?.[k] ?? 0} onChange={(e) => setRoutingWeight(k, e.target.value)} className="w-14 bg-black/40 border border-white/15 rounded-sm px-2 py-0.5 text-white text-right" />
                </label>
              ))}
            </div>
            <label className="text-[11px] uppercase tracking-wider text-white/50 block">Default / fallback family
              <select data-testid="ai-routing-default" value={cfg.routing?.default_family || "clean_modern_performance"} onChange={(e) => setRoutingDefault(e.target.value)} className="w-full mt-1 bg-pgt-bg border border-white/15 rounded-sm px-2 py-1 text-white text-xs">
                {FAMILY_KEYS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </label>
          </Card>

          <Card className="p-4 space-y-3" data-testid="ai-prompt-motifs">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-wider text-pgt-green">Motif Instructions</div>
              <select data-testid="ai-motif-select" value={motifSel} onChange={(e) => setMotifSel(e.target.value)} className="bg-pgt-bg border border-white/15 rounded-sm px-2 py-1 text-white text-xs">
                {MOTIF_KEYS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>
            <Field testid="ai-motif-prompt" label={MOTIF_KEYS.find(([k]) => k === motifSel)?.[1]} value={cfg.motifs?.[motifSel]} onChange={(v) => setMotifTxt(motifSel, v)} rows={4} />
          </Card>

          <Card className="p-4 space-y-3" data-testid="ai-prompt-markings">
            <div className="text-xs uppercase tracking-wider text-pgt-green">Marking Prompts</div>
            {MARK_GLOBALS.map(([k, lbl]) => <Field key={k} testid={`ai-mark-${k}`} label={lbl} value={cfg.markings?.[k]} onChange={(v) => setMkGlobal(k, v)} rows={k === "orient" ? 5 : 3} />)}
            <div className="flex items-center justify-between pt-1">
              <div className="text-[11px] uppercase tracking-wider text-white/50">Per-Style Marking Body</div>
              <select data-testid="ai-mark-body-select" value={bodySel} onChange={(e) => setBodySel(e.target.value)} className="bg-pgt-bg border border-white/15 rounded-sm px-2 py-1 text-white text-xs">
                {MARK_BODY_KEYS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>
            <p className="text-[11px] text-white/40">Use {"{orient}"} where you want the critical yard-line geometry block injected.</p>
            <Field testid="ai-mark-body-prompt" label={MARK_BODY_KEYS.find(([k]) => k === bodySel)?.[1]} value={cfg.markings?.bodies?.[bodySel]} onChange={(v) => setMkBody(bodySel, v)} rows={5} />
          </Card>

          <Card className="p-4 space-y-3" data-testid="ai-prompt-structure">
            <div className="text-xs uppercase tracking-wider text-pgt-green">Structure Prompts</div>
            <div className="text-[11px] uppercase tracking-wider text-white/50">Number Position</div>
            {MARK_POSITION_KEYS.map(([k, lbl]) => <Field key={k} testid={`ai-pos-${k}`} label={lbl} value={cfg.markings?.positions?.[k]} onChange={(v) => setMkPos(k, v)} rows={2} />)}
            <div className="text-[11px] uppercase tracking-wider text-white/50 pt-1">Start / Finish</div>
            {MARK_SF_KEYS.map(([k, lbl]) => <Field key={k} testid={`ai-sf-${k}`} label={lbl} value={cfg.markings?.startfinish?.[k]} onChange={(v) => setMkSf(k, v)} rows={2} />)}
            <div className="text-[11px] uppercase tracking-wider text-white/50 pt-1">Lane Structure</div>
            {STRUCTURE_KEYS.map(([k, lbl]) => <Field key={k} testid={`ai-struct-${k}`} label={lbl} value={cfg.structure?.[k]} onChange={(v) => setStruct(k, v)} rows={2} />)}
          </Card>
        </div>

        {/* SAMPLE + LIVE PREVIEW */}
        <div className="space-y-4">
          <Card className="p-4 space-y-3">
            <div className="text-xs uppercase tracking-wider text-pgt-green">Sample Customer Selections</div>
            <div className="flex gap-2 items-center flex-wrap text-xs text-white/70">
              <span>Size</span>
              <input data-testid="ai-sample-length" type="number" value={sample.dimensions.length} onChange={(e) => setS({ dimensions: { ...sample.dimensions, length: parseFloat(e.target.value) || 0 } })} className="w-16 bg-pgt-bg border border-white/15 rounded-sm px-2 py-1 text-white" />m ×
              <input data-testid="ai-sample-width" type="number" step="0.5" value={sample.dimensions.width} onChange={(e) => setS({ dimensions: { ...sample.dimensions, width: parseFloat(e.target.value) || 0 } })} className="w-16 bg-pgt-bg border border-white/15 rounded-sm px-2 py-1 text-white" />m
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <label className="text-white/60">Direction
                <select data-testid="ai-sample-direction" value={sample.direction} onChange={(e) => setS({ direction: e.target.value })} className="w-full mt-1 bg-pgt-bg border border-white/15 rounded-sm px-2 py-1 text-white">
                  {DIRECTION_OPTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </label>
              <label className="text-white/60">Marking Style
                <select data-testid="ai-sample-markstyle" value={sample.markings.style} onChange={(e) => setMk({ style: e.target.value })} className="w-full mt-1 bg-pgt-bg border border-white/15 rounded-sm px-2 py-1 text-white">
                  {MARK_OPTS.map((k) => <option key={k} value={k}>{k}</option>)}
                </select>
              </label>
              <label className="text-white/60">Motif
                <select data-testid="ai-sample-motif" value={sample.motif} onChange={(e) => setS({ motif: e.target.value })} className="w-full mt-1 bg-pgt-bg border border-white/15 rounded-sm px-2 py-1 text-white">
                  {MOTIF_KEYS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </label>
            </div>
            <div className="flex gap-2 flex-wrap items-center">
              <span className="text-xs text-white/60">Interval</span>
              {[1, 2].map((iv) => <Pill key={iv} on={sample.markings.interval === iv} onClick={() => setMk({ interval: iv })}>{iv}m</Pill>)}
              <span className="text-xs text-white/60 ml-2">Position</span>
              {["centre", "both_sides", "lane_based"].map((p) => <Pill key={p} on={sample.markings.position === p} onClick={() => setMk({ position: p })}>{p}</Pill>)}
            </div>
            <div className="flex gap-2 flex-wrap items-center">
              <span className="text-xs text-white/60">Intensity</span>
              {["clean", "balanced", "bold"].map((p) => <Pill key={p} on={sample.intensity === p} onClick={() => setS({ intensity: p })}>{p}</Pill>)}
              <span className="text-xs text-white/60 ml-2">Layout</span>
              {["single", "two_lane"].map((p) => <Pill key={p} on={sample.layout === p} onClick={() => setS({ layout: p })}>{p}</Pill>)}
            </div>
            <div className="flex gap-2 flex-wrap items-center">
              {["start_line", "finish_line", "sf_text"].map((k) => <Pill key={k} on={sample.markings[k]} onClick={() => setMk({ [k]: !sample.markings[k] })}>{k.replace("_", " ")}</Pill>)}
              <Pill on={sample.has_logo} onClick={() => setS({ has_logo: !sample.has_logo })} data-testid="ai-sample-logo">logo</Pill>
            </div>
            <div className="flex items-center gap-2 text-xs text-white/60">
              <span>Brand hexes</span>
              <input data-testid="ai-sample-brand" value={sample.brand_colours.join(",")} onChange={(e) => setS({ brand_colours: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} className="flex-1 bg-pgt-bg border border-white/15 rounded-sm px-2 py-1 text-white font-mono" />
            </div>
            <input data-testid="ai-sample-instructions" value={sample.instructions} onChange={(e) => setS({ instructions: e.target.value.slice(0, 160) })} placeholder="Optional customer note" className="w-full bg-pgt-bg border border-white/15 rounded-sm px-2 py-1 text-white text-xs" />
          </Card>

          <Card className="p-4 space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-wider text-pgt-green">Live Assembled Prompt — exactly what GPT Image 2 receives</div>
              {preview && <span className="text-[10px] text-white/40">{preview.length} chars · {preview.resolved_family}{preview.ref_image ? ` · ref:${preview.ref_image}` : " · no ref"}</span>}
            </div>
            <pre data-testid="ai-prompt-preview" className="whitespace-pre-wrap bg-pgt-bg border border-pgt-green/30 rounded-sm p-3 text-white/85 text-xs leading-relaxed max-h-[420px] overflow-auto font-mono">{preview ? preview.prompt : "…"}</pre>
            {preview && preview.requested_direction === "ai_choice" && <p className="text-[10px] text-white/40">AI Designer's Choice previewed as Clean Modern Performance (live it randomly picks an approved family).</p>}
          </Card>
        </div>
      </div>

      {/* EFFECTIVE PROMPTS VIEWER */}
      <Card className="p-4 space-y-2" data-testid="ai-effective-prompts">
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-wider text-pgt-green">Stored Effective Prompts (recent generations)</div>
          <button onClick={refreshEff} className="text-white/50 text-xs hover:text-white">Refresh</button>
        </div>
        {!effective.length && <p className="text-white/40 text-xs">No generations recorded yet.</p>}
        <div className="space-y-1">
          {effective.map((e, i) => (
            <div key={i} className="border border-white/10 rounded-sm">
              <button onClick={() => setOpenEff(openEff === i ? null : i)} className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left">
                <span className="text-white/80 text-xs">{e.style_family || e.direction} · {e.dimensions?.length}×{e.dimensions?.width}m · {e.markings?.style || "no marks"} · {(e.brand_colours || []).slice(0, 3).join(" ")}</span>
                <span className="text-white/30 text-[10px]">{(e.created_at || "").slice(0, 16).replace("T", " ")}</span>
              </button>
              {openEff === i && (
                <div className="px-3 pb-3 space-y-1">
                  <div className="text-[10px] text-white/40">family={e.style_family || e.direction} · ref={String(e.ref_image)} · motif={e.motif_resolved || e.motif} · requested={(e.brand_colours_requested || []).join(" ") || "—"} · logo={String(e.has_logo)}</div>
                  <pre className="whitespace-pre-wrap bg-pgt-bg border border-white/10 rounded-sm p-2 text-white/70 text-[11px] leading-relaxed max-h-64 overflow-auto font-mono">{e.prompt}</pre>
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

